/* global window document pymChild */

import './styles/graphic.scss';
import * as axios from 'axios';
import { Child } from 'pym.js';
import { select } from 'd3-selection';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'regenerator-runtime/runtime';

// ////// ENTER URL for data processed by cronbot
let map_data_url = 'https://ix.cnn.io/data/severe-storms/day1otlk_cat.nolyr.json';

// ////// ENTER details for map styles
const config = {
  style: 'mapbox://styles/cnndigital/ckwo2uifi0oj414s1d56pqdvh',
  default_zoom: 3.2,
};

// >> ENTER graphic slug, with date ... ie "20210908-stream-flood-tracker"
const graphic_slug = '20220322-storm-outlook-live';

// date
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.tz.setDefault('America/New_York');
dayjs.extend(updateLocale);
dayjs.updateLocale('en', {
  monthsShort: [
    'Jan.', 'Feb.', 'March', 'April', 'May', 'June',
    'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
  ],
  meridiem: (hour, minute, isLowercase) => (hour >= 12 ? 'p.m.' : 'a.m.'),
});

// Mapbox
const mapboxgl = require('mapbox-gl');

const MAPBOX_TOKEN = 'pk.eyJ1IjoiY25uZGlnaXRhbCIsImEiOiJja2V1M3Vyd3kwMDZrMnF0Njdhb3ZhZDk5In0.OURIYzTcS6ZzoWjXJzfANg';
const defaultCenter = [-95.7129, 39];
let map;
let mapParams;
let hasLocation;
let daySelector; // defaults to today if not set elsewhere


// instantiate pym object
window.pymChild = new Child();

// check for URL params
function checkParams() {
  // use the parent URL if we're using the "parent.html" preview page
  // otherwise use the straight url
  let url;
  if ((pymChild.parentUrl) && (pymChild.parentUrl.indexOf('/parent.html') !== -1)) {
    url = pymChild.parentUrl;
  } else {
    url = window.location.href;
  }

  console.log('Parent url:', pymChild.parentUrl);
  console.log('Using url:', url);

  // Parse params
  const getParams = function (url) {
    const params = {};
    const parser = document.createElement('a');
    parser.href = url;
    const query = parser.search.substring(1);
    const vars = query.split('&');
    for (let i = 0; i < vars.length; i++) {
      const pair = vars[i].split('=');
      params[pair[0]] = decodeURIComponent(pair[1]);
    }
    return params;
  };

  mapParams = getParams(url);
  if ('lnglat' in mapParams) {
    mapParams.lnglat = [parseFloat(mapParams.lnglat.split(',')[0]), parseFloat(mapParams.lnglat.split(',')[1])];
  }
  if ('z' in mapParams) {
    mapParams.z = parseFloat(mapParams.z);
  }
  if ('l' in mapParams) {
    mapParams.l = mapParams.l.split(',');
  }

  // Set params to true if they have what we want (zoom + lng/lat)
  hasLocation = !!('lnglat' in mapParams && 'z' in mapParams);
}

export default function (props, container, isMobile, meta) {
  // function to get the latest map data
  const fetchData = async (data_url) => {
    const response = await axios.get(data_url);
    return response.data;
  };

  // function to draw the map
  const drawMap = (mapdata_full) => {
    const {
      initialWidth,
      initialHeight,
      // width,
      // height
    } = props;


    // filter out the general thunderstorm object
    const mapdata = {
      type: mapdata_full.type,
      features: mapdata_full.features.filter(d => d.properties.LABEL != 'TSTM'),
    };


    // // -- dynamic header -- // //

    // note ... using mapdata_full so we have a timestamp even if we've filtered out the
    //          only object because it was TSTM
    const forecastDay = dayjs.utc(mapdata_full.features[0].properties.VALID, 'YYYYMMDDHHmm').tz('America/New_York').format('dddd');

    const headline = (daySelector) ? meta.headline.replace('Today', forecastDay) : meta.headline;

    const header = select('#meta');
    header.html('');

    if (!(mapParams.headless)) {
      header.append('h1')
        .attr('class', 'headline')
        .html(headline);

      header.append('h2')
        .attr('class', 'dek')
        .html(meta.subhed);
    }


    // // -- legend code -- // //


    const legend_title = `Severe weather risk ${daySelector ? forecastDay : 'today'}`;
    const color_width = 70; // width in pixels
    const breaks = ['Low', '', '', '', 'High'];
    const colors = [
      '#66A366',
      '#FFE066',
      '#FFA366',
      '#E06666',
      '#FF72F9',
    ];

    // assumes a previously-established container div
    container
      .append('div')
      .attr('class', 'v-legend-hed')
      .html(legend_title);

    const map_legend = container
      .append('div')
      .attr('class', 'v-legend-colors-container')
      .attr('width', initialWidth);

    map_legend.selectAll('.v-legend-colors')
      .data(colors)
      .enter()
      .append('div')
      .attr('class', 'v-legend-colors')
      .style('height', '8px')
      .style('width', `${color_width}px`)
      .style('background-color', d => d)
      .style('opacity', 0.75);

    const map_legend_labels = container
      .append('div')
      .attr('class', 'v-legend-labels-container')
      .attr('width', initialWidth);

    map_legend_labels
      .append('div')
      .attr('class', 'v-legend-label-spacer')
      .style('width', `${color_width / 2}px`);

    map_legend_labels.selectAll('.v-legend-labels')
      .data(breaks)
      .enter()
      .append('div')
      .attr('class', 'v-legend-labels')
      .style('width', `${color_width}px`)
      .html(d => d);

    map_legend_labels
      .append('div')
      .attr('class', 'v-legend-label-spacer')
      .style('width', `${color_width / 2}px`);

    // // -- Map drawing -- // //

    console.log('using cnnvis-cronbot data:', mapdata);

    checkParams();

    // complicated way to adjust the zoom depending on the
    // map size. could use more elegance.
    function getZoom() {
      let zoomLevel;
      if (hasLocation) {
        zoomLevel = isMobile ? mapParams.z - 1 : mapParams.z;
      } else if (initialWidth >= 900) {
        zoomLevel = config.default_zoom + 0.25;
      } else if (initialWidth <= 320) {
        zoomLevel = 2;
      } else {
        zoomLevel = isMobile ? config.default_zoom - 1 : config.default_zoom;
      }
      return zoomLevel;
    }

    // Draw the map
    container.append('div')
      .attr('class', 'map-container')
      .append('div')
      .attr('id', 'map')
      .attr('width', initialWidth)
      .attr('height', initialHeight);

    // Dynamic footer
    const as_of = dayjs.utc(mapdata_full.features[0].properties.ISSUE, 'YYYYMMDDHHmm').tz('America/New_York').format('dddd, MMM D, YYYY [at] h:mm a [ET]');
    const valid_from = dayjs.utc(mapdata_full.features[0].properties.VALID, 'YYYYMMDDHHmm').tz('America/New_York').format('dddd, MMM D, [at] h:mm a [ET]');
    const valid_until = dayjs.utc(mapdata_full.features[0].properties.EXPIRE, 'YYYYMMDDHHmm').tz('America/New_York').format('dddd, MMM D, [at] h:mm a [ET]');
    container.append('div')
      .attr('class', 'v-dynamic-footnote')
      // .html(`&nbsp;<br>${meta.footnote} Data as of ${as_of.trim()}.`);
      .html(`&nbsp;<br>Risk outlook from ${valid_from} to ${valid_until}. Updated ${as_of.trim()}.`);

    mapboxgl.accessToken = MAPBOX_TOKEN;
    map = new mapboxgl.Map({
      container: 'map',
      style: config.style,
      projection: { name: 'albers' },
      center: hasLocation ? mapParams.lnglat : defaultCenter,
      zoom: getZoom(),
      maxZoom: 7,
      minZoom: 2,
      //   Interactive disabled
      pitchWithRotate: false,
      scrollZoom: false,
      dragRotate: false,
      touchPitch: false,
      interactive: true,
      cooperativeGestures: true,
    });

    // ////// LOAD YOUR DATA GEOJSON HERE /////////

    map.on('load', () => {
      pymChild.sendHeight();

      if (isMobile) {
        map.setPaintProperty('state-innerlines', 'line-opacity', 0.5);
      }

      // zoom buttons
      map.addControl(new mapboxgl.NavigationControl({
        showCompass: false,
      }), 'top-right');

      // // scale
      // map.addControl(new mapboxgl.ScaleControl({
      //   maxWidth: 100,
      //   unit: 'imperial',
      // }), 'top-left');

      // // -- load data layer here -- // //

      if (isMobile) {
        map.setPaintProperty('state-innerlines', 'line-opacity', 0.5);
      }

      // Example using local geojson and opacity fill
      map.addSource('county', {
        type: 'geojson',
        data: mapdata,
      });

      map.addLayer({
        id: 'county',
        source: 'county',
        type: 'fill',
        paint: {
          'fill-color': ['get', 'fill'],
          'fill-opacity': 0.75,
        },

      // Layer in the style stack under which this layer should be drawn
      // (We're using waterway-label, which is below all the state and city names)
      }, 'jk-us-outline-stroke');
    });

    // make state lines lighter on mobile when zoomed out
    map.on('zoom', () => {
      if (isMobile && map.getZoom() < 4) {
        map.setPaintProperty('state-innerlines', 'line-opacity', 0.5);
      } else {
        map.setPaintProperty('state-innerlines', 'line-opacity', 1);
      }
    });

    // /////// THIS PUTS EMBED CODES INTO THE CONSOLE LOG ////////
    // Note that these are removed in the

    map.on('idle', () => {
      console.log('- - - - - - - - - -');
      const current_lon = map.getCenter().lng;
      const current_lat = map.getCenter().lat;
      const current_zoom = map.getZoom();
      let new_params = `?lnglat=${current_lon},${current_lat}&z=${current_zoom}`;
      if (daySelector) {
        new_params += `&day=${mapParams.day}`;
      }
      if (mapParams.headless) {
        new_params += '&headless=true';
      }
      const time_hash = Date.now().toString(36);

      console.log('DLE link:');
      console.log(`https://ix.cnn.io/dailygraphics/graphics/${graphic_slug}/index.html${new_params}`);
      console.log('Web tag:');
      console.log(`<div id="responsive-embed-${graphic_slug}-${time_hash}"></div>
      <script>
      if(!window.pym){(function(b,c){var d=document.createElement("script");d.type="text/javascript",d.readyState?d.onreadystatechange=function(){("loaded"===d.readyState||"complete"===d.readyState)&&(d.onreadystatechange=null,c())}:d.onload=function(){c()},d.src=b,document.getElementsByTagName("head")[0].appendChild(d)})("//cdn.cnn.com/cnn/.e/interactive/js/lib/vendor/pym/pym.v1.min.js",function(){new pym.Parent("responsive-embed-${graphic_slug}-${time_hash}","//ix.cnn.io/dailygraphics/graphics/${graphic_slug}/index.html${new_params}",{title:"CNN Graphic"})})}else var responsiveEmbed=new pym.Parent("responsive-embed-${graphic_slug}-${time_hash}","//ix.cnn.io/dailygraphics/graphics/${graphic_slug}/index.html${new_params}",{title:"CNN Graphic"});
      </script>`);

      // if we have a day=YYYY-MM-DD parameter, output a suggested slug
      if (daySelector == 'dailies') {
        const slugdate = dayjs(mapParams.day, 'YYYY-MM-DD').format('ddd-YYYYMMMD').toLowerCase();
        console.log('Suggested slug:');
        console.log(`storm-outlook-${slugdate}-wx-live`);
      }
    });

    // update height of svg to pym
    pymChild.sendHeight();
  };

  checkParams();

  if (mapParams.day && mapParams.day.toLowerCase().trim() == 'tomorrow') {
    map_data_url = 'https://ix.cnn.io/data/severe-storms/day2otlk_cat.nolyr.json';
    daySelector = 'tomorrow';
  }

  if (mapParams.day && mapParams.day.trim().match(/^\d\d\d\d-\d\d-\d\d$/)) {
    map_data_url = `https://ix.cnn.io/data/severe-storms/dailies/${mapParams.day}.json`;
    daySelector = 'dailies';
  }


  // fetch data and draw the map
  fetchData(map_data_url).then(drawMap);
}
